/* ========== CHART RENDERER ORCHESTRATOR ==========
 * Thin dispatcher: takes a chart object, picks the right per-type
 * renderer, and applies common padding/title/border chrome.
 *
 * Each chart family lives in its own file under this directory:
 *   - column.js     — Clustered/Stacked/100% Stacked/3-D Column
 *   - bar.js        — Clustered/Stacked/100% Stacked/3-D Bar
 *   - line.js       — Line, Stacked Line, 100% Stacked Line, 3-D Line, w/ markers
 *   - area.js       — Stacked Area, 100% Stacked Area (Area + 3-D Area use line.js)
 *   - pie.js        — Pie, 3-D Pie, Doughnut, Pie of Pie, Bar of Pie
 *   - scatter.js    — Scatter (XY), Bubble
 *   - radar.js      — Radar, Radar with Markers, Filled Radar
 *   - stock.js      — High-Low-Close, OHLC
 *   - surface.js    — Surface
 *   - waterfall.js  — Waterfall
 *   - funnel.js     — Funnel
 *   - treemap.js    — Treemap
 *   - sunburst.js   — Sunburst
 *   - boxWhisker.js — Box and Whisker
 *   - histogram.js  — Histogram, Pareto
 *   - map.js        — Filled Map
 *   - sparkline.js  — Line/Column/Win-Loss sparklines
 *   - combo.js      — Combo (mixed column + line)
 *   - utils.js      — Shared COLORS + helpers (drawAxes, drawGridlines, etc.)
 *
 * When QA reports an issue with a specific chart subtype, look in the
 * matching file and tweak just that renderer.
 * =================================================== */

import { drawColumnChart, drawStackedColumnChart } from './column';
import { drawHorizontalBarChart, drawStackedBarChart } from './bar';
import { drawLineChart, drawStackedLineChart } from './line';
import { drawStackedAreaChart } from './area';
import { drawPieChart, drawPieOfPieChart } from './pie';
import { drawScatterChart, drawBubbleChart } from './scatter';
import { drawRadarChart } from './radar';
import { drawStockChart } from './stock';
import { drawSurfaceChart } from './surface';
import { drawWaterfallChart } from './waterfall';
import { drawFunnelChart } from './funnel';
import { drawTreemapChart } from './treemap';
import { drawSunburstChart } from './sunburst';
import { drawBoxWhiskerChart } from './boxWhisker';
import { drawHistogramChart } from './histogram';
import { drawFilledMapChart } from './map';
import { drawSparklineChart } from './sparkline';
import { drawComboChart } from './combo';
import { computeLegendRows, xAxisLabelLayout, LEGEND_ROW_HEIGHT, MULTI_LEVEL_ROW_HEIGHT } from './utils';

// Chart types whose x-axis carries category labels that may rotate 45°
// when they don't fit (horizontal "bar" puts categories on the Y axis,
// histogram/pareto manage their own bin labels).
const CAT_AXIS_TYPES = { col: true, line: true, area: true, waterfall: true, boxWhisker: true };

// Re-export per-chart drawers so existing imports of these names keep working.
export {
  drawColumnChart, drawStackedColumnChart,
  drawHorizontalBarChart, drawStackedBarChart,
  drawLineChart, drawStackedLineChart,
  drawStackedAreaChart,
  drawPieChart, drawPieOfPieChart,
  drawScatterChart, drawBubbleChart,
  drawRadarChart,
  drawStockChart,
  drawSurfaceChart,
  drawWaterfallChart,
  drawFunnelChart,
  drawTreemapChart,
  drawSunburstChart,
  drawBoxWhiskerChart,
  drawHistogramChart,
  drawFilledMapChart,
  drawSparklineChart,
  drawComboChart,
};

// Chart types whose label/legend handling differs from the default
// (bar/col/line/area/scatter etc. which reserve room for x-axis labels):
//
//   TIGHT_TYPES — no bottom axis, no inline legend (labels inside slices)
//                 → maximum plot area
//   INTERNAL_LEGEND_TYPES — render their own legend below the plot
//                 → need bottom space for ~3 rows of legend
const TIGHT_TYPES = {
  sunburst: true,
  treemap: true,
  filledMap: true,
  regionMap: true,
  funnel: true,
};
const INTERNAL_LEGEND_TYPES = {
  pie: true,
  doughnut: true,
  pieOfPie: true,
  barOfPie: true,
  radar: true,
};

export function renderSimpleChart(canvas, chartObj, logicalWidth, logicalHeight) {
  const ctx = canvas.getContext('2d');
  const width = logicalWidth || canvas.width;
  const height = logicalHeight || canvas.height;
  const isSparkline = !!chartObj.isSparkline;
  const chartType = chartObj.chartType || 'bar';

  // Pick padding profile. Sparklines get nothing; tight types use almost
  // all the canvas; internal-legend types reserve a band at bottom.
  let padding;
  if (isSparkline) {
    padding = { top: 1, right: 1, bottom: 1, left: 1 };
  } else if (TIGHT_TYPES[chartType]) {
    padding = { top: 28, right: 8, bottom: 8, left: 8 };
  } else if (INTERNAL_LEGEND_TYPES[chartType]) {
    padding = { top: 28, right: 10, bottom: 44, left: 10 };
  } else {
    padding = { top: 52, right: 24, bottom: 48, left: 58 };
    // The default profile reserves room for ONE legend row and a flat
    // (non-rotated) x-axis label band. Grow the bottom padding when:
    //  a) category labels must rotate 45° to avoid overlapping, and/or
    //  b) the series legend wraps onto multiple rows.
    const plotW = Math.max(50, width - padding.left - padding.right);
    let extraBottom = 0;

    const cats = chartObj.categories || [];
    const dataLens = (chartObj.series || []).map(s => (s.data || []).length);
    const nCats = Math.max(cats.length, dataLens.length ? Math.max.apply(null, dataLens) : 0);
    if ((CAT_AXIS_TYPES[chartType] || chartObj.isCombo) && nCats > 0) {
      if (chartType === 'line' && chartObj.is3D) {
        // 3-D line: tilted floor band — vertical leaf labels (multi-level),
        // one horizontal row per parent level, plus the floor's descent.
        const leafLabels = (chartObj.catLevels && chartObj.catLevels[0]) || cats;
        ctx.font = '9px Arial';
        let maxLeafW = 0;
        for (let i = 0; i < nCats; i++) {
          const l = leafLabels && leafLabels[i] != null && leafLabels[i] !== '' ? '' + leafLabels[i] : '' + (i + 1);
          const w = ctx.measureText(l).width;
          if (w > maxLeafW) maxLeafW = w;
        }
        const vertical = !!(chartObj.catLevels && chartObj.catLevels.length > 1);
        const leafBandH = vertical ? Math.min(80, maxLeafW + 12) : 17;
        const extraLevels = chartObj.catLevels ? chartObj.catLevels.length - 1 : 0;
        // The floor's descent is absorbed INSIDE the plot height (the
        // value axis shrinks to H = plotH - plotW*downSlope), so only the
        // label band itself needs extra bottom space.
        const band = leafBandH + extraLevels * 16 + 6;
        extraBottom += Math.max(0, band - 15);
        // Room on the right for the depth-axis series name ("Sales") and
        // the gridlines extending past the plot.
        padding.right = Math.max(padding.right, 56);
      } else if (chartObj.catLevels && chartObj.catLevels.length > 1) {
        // Multi-level axis: one stacked row per level (+ separators)
        const band = 6 + chartObj.catLevels.length * MULTI_LEVEL_ROW_HEIGHT + 2;
        extraBottom += Math.max(0, band - 15);
      } else {
        const spacing = chartType === 'line'
          ? plotW / Math.max(1, nCats - 1)
          : plotW / nCats;
        const layout = xAxisLabelLayout(ctx, cats, nCats, spacing);
        extraBottom += Math.max(0, layout.band - 15);
      }
    }

    // Horizontal bar charts: category labels live on the LEFT axis — give
    // them room (leaf band + one rotated column per parent level).
    if (chartType === 'bar' && nCats > 0) {
      const leafLabels = (chartObj.catLevels && chartObj.catLevels[0]) || cats;
      ctx.font = '9px Arial';
      let maxLeafW = 0;
      for (let i = 0; i < nCats; i++) {
        const l = leafLabels && leafLabels[i] != null && leafLabels[i] !== '' ? '' + leafLabels[i] : '' + (i + 1);
        const w = ctx.measureText(l).width;
        if (w > maxLeafW) maxLeafW = w;
      }
      const extraLevels = chartObj.catLevels ? chartObj.catLevels.length - 1 : 0;
      const leafBandW = Math.min(90, maxLeafW + 14);
      padding.left = Math.max(58, Math.min(170, leafBandW + extraLevels * 16 + 10));
      if (chartObj.is3D) {
        padding.bottom += 14;                       // descending tick labels
        padding.right = Math.max(padding.right, 36); // box depth + lean
      }
    }

    const legendSeries = (chartObj.series || []).filter(s => s && s.name);
    if (legendSeries.length > 1) {
      const rows = computeLegendRows(ctx, legendSeries, plotW).length;
      if (rows > 1) extraBottom += (rows - 1) * LEGEND_ROW_HEIGHT;
    }

    if (extraBottom > 0) {
      // Cap so the plot area never collapses below ~40% of the canvas.
      padding.bottom += Math.min(extraBottom, Math.max(0, height * 0.6 - padding.top - padding.bottom));
    }
  }
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  ctx.clearRect(0, 0, width, height);
  if (!isSparkline) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Title
    ctx.fillStyle = '#333333';
    ctx.font = 'bold 13px Calibri, Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(chartObj.title || 'Chart', width / 2, 8);
  }

  if (chartObj.isCombo) {
    drawComboChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
  } else {
    const grouping = chartObj.grouping || 'standard';
    const is3D = !!chartObj.is3D;
    switch (chartType) {
      case 'col':
        if (grouping === 'stacked') {
          drawStackedColumnChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, false, is3D);
        } else if (grouping === 'percentStacked') {
          drawStackedColumnChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, true, is3D);
        } else {
          drawColumnChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, is3D);
        }
        break;
      case 'bar':
        if (grouping === 'stacked') {
          drawStackedBarChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, false, is3D);
        } else if (grouping === 'percentStacked') {
          drawStackedBarChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, true, is3D);
        } else {
          drawHorizontalBarChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, is3D);
        }
        break;
      case 'line':
        if (grouping === 'stacked') {
          drawStackedLineChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, false, !!chartObj.hasMarker);
        } else if (grouping === 'percentStacked') {
          drawStackedLineChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, true, !!chartObj.hasMarker);
        } else {
          drawLineChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, false, is3D, !!chartObj.hasMarker);
        }
        break;
      case 'pie':
        drawPieChart(ctx, width / 2, padding.top + chartHeight / 2, Math.min(chartWidth, chartHeight) / 2 - 10, chartObj, is3D);
        break;
      case 'doughnut':
        drawPieChart(ctx, width / 2, padding.top + chartHeight / 2, Math.min(chartWidth, chartHeight) / 2 - 10, chartObj, false);
        break;
      case 'pieOfPie':
        drawPieOfPieChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, false);
        break;
      case 'barOfPie':
        drawPieOfPieChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, true);
        break;
      case 'area':
        if (grouping === 'stacked') {
          drawStackedAreaChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, false);
        } else if (grouping === 'percentStacked') {
          drawStackedAreaChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, true);
        } else {
          drawLineChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, true, is3D, false);
        }
        break;
      case 'scatter':
        drawScatterChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      case 'bubble':
        drawBubbleChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      case 'radar':
        drawRadarChart(ctx, width / 2, padding.top + chartHeight / 2, Math.min(chartWidth, chartHeight) / 2 - 10, chartObj);
        break;
      case 'stock':
        drawStockChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      case 'surface':
        drawSurfaceChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      case 'waterfall':
        drawWaterfallChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      case 'funnel':
        drawFunnelChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      case 'treemap':
        drawTreemapChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      case 'sunburst':
        drawSunburstChart(ctx, width / 2, padding.top + chartHeight / 2, Math.min(chartWidth, chartHeight) / 2 - 10, chartObj);
        break;
      case 'boxWhisker':
        drawBoxWhiskerChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      case 'histogram':
        drawHistogramChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, false);
        break;
      case 'pareto':
        drawHistogramChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, true);
        break;
      case 'regionMap':
      case 'filledMap':
        drawFilledMapChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      case 'sparkline-line':
      case 'sparkline-column':
      case 'sparkline-winLoss':
        drawSparklineChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj);
        break;
      default:
        drawColumnChart(ctx, padding.left, padding.top, chartWidth, chartHeight, chartObj, is3D);
    }
  }

  // Border (skip for sparklines — they render inline in cells)
  if (!isSparkline) {
    ctx.strokeStyle = '#d0d0d0';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, width - 1, height - 1);
  }
}
